import React, { useState, useRef, useEffect } from "react";
import { Card, CardBody, Container, Spinner } from "reactstrap";
import { useNavigate, useLocation } from "react-router-dom";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import { useCRFService } from "modules/Library/CRF/api/useCRFQuery";
import {
  GetUserData,
  perPageData,
  showToastMessage,
} from "common/service/CommonService";
import CommonTableIndexWise from "Components/Common/CommonTableIndexWise";
import { queryClient } from "api/queryClient";
import CommonSearch from "../../../../Components/Common/CommonSearch";
import {
  PERMISSION_ACTIONS,
  PERMISSION_MODULES,
} from "../../../../common/constants/PermissionConstants";
import CommonButton from "../../../../Components/Common/CommonButton";
import { CRFColumn } from "../helpers/crfconstant";
import { LIBRARY_PATHS } from "Routes/paths";
import { CRFItem } from "../helpers/crf.types";
import FilePreviewModal from "../../../Study/Attachments/components/FilePreviewModal";
import { useAuditService } from "modules/Audit/api/useAuditQuery";
import CRFAssignment from "./CRFAssignment";
import styles from "../css/crfassignment.module.scss";
import CRFAssignmentFilterModal, { CRFAssignmentFilterValues } from "../components/CRFAssignmentFilterModal";
import sf from "assets/scss/common/searchFilter.module.scss";

const CRFList: React.FC = () => {
  document.title = "CRF | ZENTIXS VISTA";
  // Navigation
  const navigate = useNavigate();
  const location = useLocation();
  // Query client
  const userData = GetUserData();

  //-------------------------------------------------------------------------------------------
  //--> Top Level Tabs State (CRF | CRF Assignment)
  //-------------------------------------------------------------------------------------------
  const initialTab = (location.state as any)?.activeTab === "assignment" ? "assignment" : "crf";
  const [activeMainTab, setActiveMainTab] = useState<"crf" | "assignment">(initialTab);

  useEffect(() => {
    if ((location.state as any)?.activeTab) {
      setActiveMainTab((location.state as any).activeTab);
    }
  }, [location.state]);
  const [assignmentSearchText, setAssignmentSearchText] = useState<string>("");
  const [selectedModuleInAssignment, setSelectedModuleInAssignment] = useState<any | null>(null);
  const [searchText, setSearchText] = useState<string>("");
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = useState<CRFItem | null>(null);
  const [showDeleteReason, setShowDeleteReason] = useState<boolean>(false);
  //Audit
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const { SelectAllMutation: AuditMutation } = useAuditService();

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState<CRFAssignmentFilterValues>({});
  const [isFilterApplied, setIsFilterApplied] = useState(false);

  const toggleFilter = () => setIsFilterOpen((prev) => !prev);

  const applyFilters = (values: CRFAssignmentFilterValues) => {
    if (!values.category) {
      setFilters({});
      setIsFilterApplied(false);
    } else {
      setFilters(values);
      setIsFilterApplied(true);
    }
  };

  const clearFilters = () => {
    setFilters({});
    setIsFilterApplied(false);
  };

  const toggleDeleteModal = (): void => setDeleteModal(!deleteModal);

  // CRF Service(s)
  const { SelectAllQuery, DeleteMutation } = useCRFService({
    // otherid: null,
    searchText,
    pageNumber: currentPageNumber,
    pageSize: perPageData,
    enabledSelectAll: activeMainTab === "crf",
    enabledCategorySelectAll: false,
  });

  //-------------------------------------------------------------------------------------------
  //--> Page Start
  //-------------------------------------------------------------------------------------------

  const {
    data: queryResponse,
    isLoading,
    isError,
    isFetching,
  } = SelectAllQuery as {
    data?: { data?: (CRFItem & { total_count: number })[] };
    isLoading: boolean;
    isFetching: boolean;
    isError: boolean;
  };

  const data: (CRFItem & { total_count: number })[] = queryResponse?.data ?? [];
  const totalCount = data[0]?.total_count || 0;

  useEffect(() => {
    const maxPages = Math.max(1, Math.ceil(totalCount / perPageData));
    if (currentPageNumber > maxPages) {
      setCurrentPageNumber(maxPages);
    }
  }, [totalCount, currentPageNumber]);

  //-------------------------------------------------------------------------------------------
  //--> ON SEARCH
  //-------------------------------------------------------------------------------------------

  const onSearch = (text: string | null) => {
    const next = (text ?? "").trim();

    if (next === searchText && currentPageNumber === 1) return;

    setCurrentPageNumber(1);
    setSearchText(next);
  };
  //-------------------------------------------------------------------------------------------
  //--> On View
  //-------------------------------------------------------------------------------------------

  const onView = (item: CRFItem): void => {
    localStorage.setItem("currentCRFType", "masterCRF");
    navigate(`/crf/view/${item.crfid}`, { state: item });
  };

  //-------------------------------------------------------------------------------------------
  //--> On Edit
  //-------------------------------------------------------------------------------------------

  const onEdit = (item: CRFItem) => {
    localStorage.setItem("crfForEdit", JSON.stringify(item));
    navigate(`/crf/edit/${item.crfid}`, { state: item });
  };

  //-------------------------------------------------------------------------------------------
  //--> Record Delete
  //------------------------------------------------------------------------------------------

  const onDeleteClick = (item: CRFItem): void => {
    setDeleteModal(true);
    setSelectedRecord(item);
  };
  const ToggleDeleteResason = (): void => {
    setShowDeleteReason((prev) => !prev);
  };

  const confirmDelete = (reasonPayload: {
    id: number | null;
    reason: string;
  }): void => {
    if (!selectedRecord) return;
    const payload = {
      crfid: reasonPayload.id,
      updatereason: reasonPayload.reason,
    };
    DeleteMutation.mutate(payload, {
      onSuccess: (response) => {
        showToastMessage("success", response?.message);

        const nextPage = 1;
        const nextSearch = "";

        const isAlreadyFinal =
          currentPageNumber === nextPage &&
          (searchText ?? "").trim() === nextSearch;

        if (isAlreadyFinal) {
          queryClient.invalidateQueries({
            queryKey: ["crf-selectAll"],
            refetchType: "active",
          });
        } else {
          queryClient.cancelQueries({ queryKey: ["crf-selectAll"] });
          setCurrentPageNumber(nextPage);
          setSearchText(nextSearch);
        }

        toggleDeleteModal();
        setSelectedRecord(null);
        ToggleDeleteResason();
      },
      onError: (error: Error) => {
        showToastMessage("error", error?.message ?? error);
      },
    });
  };

  const onAuditClick = () => {
    const moduleName = activeMainTab === "crf" ? "CRF" : "CRF Assignment";
    const payload = {
      modulename: moduleName,
    };

    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data instanceof Blob) {
          const pdfFile = new File([data], `${moduleName.replace(/\s+/g, "_")}_Audit_Log.pdf`, {
            type: "application/pdf",
          });
          setPreviewFile(pdfFile);
          setIsPreviewOpen(true);
        }
      },
      onError: (error: any) => {
        showToastMessage("error", "Error fetching audit logs");
        console.error("Error fetching audit logs:", error);
      },
    });
  };

  return (
    <div className="page-content">
      <Container fluid className="px-0">
        <Card className="table_wrapper mb-0">
          <CardBody>
            <div className="d-flex justify-content-between align-items-center">
              {/* Premium Tab Segmented Switcher (matching VolunteerList.tsx) */}
              <div
                className="d-inline-flex align-items-center justify-content-center"
                style={{
                  backgroundColor: "#f3f6f9",
                  borderRadius: "50px",
                  padding: "4px",
                  border: "1px solid #e9ebec",
                }}
              >
                <CommonButton
                  module={PERMISSION_MODULES.CRF}
                  action={PERMISSION_ACTIONS.VIEW}
                  className={`btn btn-sm px-4 rounded-pill fw-semibold border-0 ${activeMainTab === "crf" ? "text-primary shadow-sm" : "text-muted"
                    }`}
                  style={{
                    backgroundColor: activeMainTab === "crf" ? "#ffffff" : "transparent",
                    transition: "all 0.2s ease-in-out",
                  }}
                  onClick={() => setActiveMainTab("crf")}
                >
                  <i className="ri-file-text-line me-1" /> CRF
                </CommonButton>
                <CommonButton
                  module={PERMISSION_MODULES.CRF}
                  action={PERMISSION_ACTIONS.VIEW}
                  className={`btn btn-sm px-4 rounded-pill fw-semibold border-0 ${activeMainTab === "assignment" ? "text-primary shadow-sm" : "text-muted"
                    }`}
                  style={{
                    backgroundColor: activeMainTab === "assignment" ? "#ffffff" : "transparent",
                    transition: "all 0.2s ease-in-out",
                  }}
                  onClick={() => setActiveMainTab("assignment")}
                >
                  <i className="ri-task-line me-1" /> CRF Assignment
                </CommonButton>
              </div>

              {/* Action Buttons & Search on main top row */}
              <div className="d-flex align-items-center gap-2">
                {activeMainTab === "crf" ? (
                  <>
                    <CommonSearch onSearch={onSearch} searchText={searchText} placeholder="Search CRF..." />
                    <CommonButton
                      module={PERMISSION_MODULES.CRF}
                      action={PERMISSION_ACTIONS.AUDIT}
                      color="primary"
                      size="sm"
                      onClick={onAuditClick}
                      disabled={AuditMutation.isPending}
                      outline
                    >
                      {AuditMutation.isPending ? (
                        <Spinner size="sm" className="me-2" />
                      ) : (
                        <i className="ri-history-line me-1"></i>
                      )}
                      Audit
                    </CommonButton>
                    <CommonButton
                      color="primary"
                      onClick={() => navigate(LIBRARY_PATHS.CRF_ADD)}
                      size="sm"
                      outline
                      module={PERMISSION_MODULES.CRF}
                      action={PERMISSION_ACTIONS.ADD}
                    >
                      <i className="ri-add-line me-1"></i> Add
                    </CommonButton>
                  </>
                ) : (
                  <>
                    <div className={sf.wrapper}>
                      <CommonSearch
                        onSearch={(val) => setAssignmentSearchText(val || "")}
                        searchText={assignmentSearchText}
                        placeholder="Search..."
                      />
                      {!selectedModuleInAssignment && (
                        <button
                          className={`${sf.filterBtn} ${isFilterApplied ? sf.active : ""}`}
                          onClick={toggleFilter}
                          title="Filter CRF Module"
                        >
                          <i className="ri-filter-3-line" style={{ fontSize: 15 }} />
                          {isFilterApplied && <span className={sf.filterDot} />}
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            <div key={activeMainTab} className={`${styles.tabContentArea} mt-3`}>
              {activeMainTab === "crf" ? (
                <CommonTableIndexWise
                  moduleName={PERMISSION_MODULES.CRF}
                  columns={CRFColumn}
                  data={data}
                  actions={{
                    onView: onView,
                    onDelete: onDeleteClick,
                    onEdit: onEdit,
                  }}
                  loading={isLoading || isFetching}
                  pageSize={perPageData}
                  currentPage={currentPageNumber}
                  onPageChange={setCurrentPageNumber}
                  totalRecords={data[0]?.total_count || 0}
                />
              ) : (
                <CRFAssignment
                  searchText={assignmentSearchText}
                  onSearch={(val) => setAssignmentSearchText(val || "")}
                  onModuleSelect={(mod) => setSelectedModuleInAssignment(mod)}
                  crfcategory={filters?.category || null}
                />
              )}
            </div>
          </CardBody>
        </Card>

        {/* Delete CRF Modal */}
        {deleteModal && (
          <DeleteModal
            isopen={deleteModal}
            toggle={() => setDeleteModal(false)}
            recordId={selectedRecord?.id ?? 0}
            recordName={selectedRecord?.name}
            onDeleteClick={(payload: any) => confirmDelete(payload)}
            isLoading={DeleteMutation?.isPending}
            showDeleteReason={showDeleteReason}
            toggleDeleteReason={ToggleDeleteResason}
          />
        )}
      </Container>

      {/* File Preview Modal */}
      {isPreviewOpen && (
        <FilePreviewModal
          file={previewFile}
          isOpen={isPreviewOpen}
          size={"xl"}
          onClose={() => {
            setIsPreviewOpen(false);
            setPreviewFile(null);
          }}
        />
      )}

      {/* CRF Assignment Filter Modal */}
      <CRFAssignmentFilterModal
        isOpen={isFilterOpen}
        toggle={toggleFilter}
        onApply={applyFilters}
        onClear={clearFilters}
        existingFilters={filters}
      />
    </div>
  );
};

export default CRFList;
